import React from "react";
import moment from "moment";
import { BookingStatus } from "../bookingStatus";

interface Props {
  booking: any;
  status: BookingStatus;
  isHost: boolean;
  checkInDate: Date;
  checkOutDate: Date;
}

const Tile = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <dt className="mb-1 text-[13px] text-neutral-500 dark:text-neutral-400">{label}</dt>
    <dd className="text-base font-semibold text-neutral-900 dark:text-neutral-100">{value}</dd>
  </div>
);

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;

const StayDetails: React.FC<Props> = ({ booking: b, status, isHost, checkInDate, checkOutDate }) => {
  const canceled = status === "canceled";
  const months = Number(b?.no_of_months) || 0;
  const minimum = b?.property?.minimum_months_of_booking;
  const hasMin = minimum != null && minimum !== "";
  const below = hasMin && months > 0 && months < Number(minimum);
  // The warning only matters while nothing is paid yet; after that the host has accepted the stay.
  const unpaid = status === "booking_request" || status === "invoice_sent";
  const belowUnpaid = below && unpaid;
  const shared = b?.is_shared_property === true || b?.is_shared_property === "true";
  const guests = Number(b?.number_of_guests);
  const invoiceNo = b?.invoiceNo ?? b?.invoice?.id;

  return (
    <section className={`rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5 sm:p-7 ${canceled ? "opacity-70" : ""}`}>
      <h2 className="mb-5 text-lg font-semibold text-neutral-900 dark:text-white">{canceled ? "Canceled stay" : isHost ? "Their stay" : "Your stay"}</h2>

      {belowUnpaid && (
        <div role="note" className="mb-5 rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-900 dark:text-amber-200">
          <p className="font-semibold">Minimum stay: {plural(Number(minimum), "month")}</p>
          <p className="mt-0.5">
            {isHost
              ? `This request is ${plural(months, "month")}, below your minimum stay. You can discuss it with the guest before sending an invoice.`
              : `Your stay is ${plural(months, "month")}. This listing asks for at least ${plural(Number(minimum), "month")}. The host can still accept it. Message them if you'd like to adjust your dates.`}
          </p>
        </div>
      )}

      <dl className="grid grid-cols-2 gap-x-8 gap-y-6">
        <Tile label="Check-in" value={moment(checkInDate).format("ddd, MMM D, YYYY")} />
        <Tile label="Check-out" value={moment(checkOutDate).format("ddd, MMM D, YYYY")} />
        {months > 0 && <Tile label="Length of stay" value={plural(months, "month")} />}
        <Tile label="Guests" value={Number.isFinite(guests) && guests > 0 ? plural(guests, "guest") : "-"} />
        {invoiceNo != null && invoiceNo !== "" && <Tile label="Invoice no." value={`#${invoiceNo}`} />}
        {shared && b?.booked_room && <Tile label="Room" value={b.booked_room} />}
        {!shared && b?.property?.rooms_count ? <Tile label="Total rooms" value={b.property.rooms_count} /> : null}
        <Tile label="Listing type" value={shared ? "Shared property" : "Entire property"} />
        {hasMin && !belowUnpaid && <Tile label="Minimum stay" value={plural(Number(minimum), "month")} />}
      </dl>
    </section>
  );
};

export default StayDetails;
